/**
 *  @swagger
 *  definitions:
 *    RegattaEntryCheckParams:
 *      type: object
 *      properties:
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        RegattaCheckId:
 *          description: ID of the associated RegattaCheck
 *          type: string
 *        Value:
 *          description: Value of the RegattaEntryCheck
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCheckCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCheckParams'
 *        - type: object
 *          required:
 *            - RegattaEntryId
 *            - RegattaCheckId
 *            - Value
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCheckQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaEntryCheck
 *              type: string
 *        - $ref: '#/definitions/RegattaEntryCheckParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCheckUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCheckQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaEntryCheck:
 *      allOf:
 *        - $ref: '#/definitions/RegattaEntryCheckCreateParams'
 *        - $ref: '#/definitions/RegattaEntryCheckUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaEntryCheck:
 *        allOf:
 *          - $ref: '#/definitions/RegattaEntryCheck'
 *              RegattaEntry:
 *                description: RegattaEntry of the RegattaEntryCheck
 *                $ref: '#/components/schemas/RegattaEntry'
 *              RegattaCheck:
 *                description: RegattaCheck of the RegattaEntryCheck
 *                $ref: '#/components/schemas/RegattaCheck'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaCheckId: string;
  RegattaEntryId: string;
};

export type $Params = {
  Value: boolean;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaCheck?: $Model<'RegattaCheck'>;
  RegattaEntry?: $Model<'RegattaEntry'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Value: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaEntryCheck'> => {
  const RegattaEntryCheck = stack.define(
    'RegattaEntryCheck',
    fields(Sequelize),
    {
      tableName: 'RegattaEntryChecks',
    },
  );

  RegattaEntryCheck.associate = (models: $ModelClasses) => {
    models.RegattaEntryCheck.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaEntryCheck.belongsTo(
      models.RegattaCheck,
      {
        as: 'RegattaCheck',
        foreignKey: 'RegattaCheckId',
      },
    );
  };

  RegattaEntryCheck.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaEntryCheck;
});
